<template>
    <a-modal
        :visible="visible"
        :width="1080"
        :maskClosable="false"
        title="选择商品"
        class="mall-v2-goods-picker"
        @cancel="close"
    >
        <div class="goods-picker-layout">
            <div class="goods-picker-main">
                <div class="goods-picker-toolbar">
                    <a-input-search
                        v-model="query.keyword"
                        class="goods-search"
                        placeholder="搜索商品名称 / 货号"
                        enter-button
                        @search="searchGoods"
                    />
                    <a-select
                        :value="query.storeGoodsTypeId"
                        class="goods-category"
                        allowClear
                        placeholder="全部分类"
                        @change="changeCategory"
                    >
                        <a-select-option v-for="item in categoryList" :key="item.value" :value="item.value">
                            {{ item.text || item.label }}
                        </a-select-option>
                    </a-select>
                    <a-button icon="reload" @click="searchGoods">刷新</a-button>
                </div>

                <a-spin :spinning="loading">
                    <div v-if="goodsList.length" class="goods-grid">
                        <div
                            v-for="item in goodsList"
                            :key="item.goodsId"
                            class="goods-card"
                            :class="{ active: isSelected(item.goodsId) }"
                            @click="toggleGoods(item)"
                        >
                            <div class="goods-thumb">
                                <img v-if="item.image" :src="image(item.image)" :alt="item.goodsName" />
                                <a-icon v-else type="picture" />
                            </div>
                            <div class="goods-info">
                                <div class="goods-name">{{ item.goodsName || '未命名商品' }}</div>
                                <div class="goods-no">{{ item.goodsNo || item.goodsId }}</div>
                                <div class="goods-price">¥{{ getGoodsPrice(item) }}</div>
                            </div>
                            <a-tag v-if="isSelected(item.goodsId)" color="red" class="goods-selected-tag">已选</a-tag>
                        </div>
                    </div>
                    <a-empty v-else description="暂无商品，请调整搜索条件" />
                </a-spin>

                <div class="goods-load-more">
                    <a-button v-if="hasMore" :loading="loadingMore" @click="loadMore">加载更多</a-button>
                    <span v-else>已加载全部当前结果</span>
                </div>
            </div>

            <div class="goods-selected-panel">
                <div class="selected-header">
                    <span>已选商品</span>
                    <a-button v-if="selectedItems.length" type="link" size="small" @click="clearSelected">清空</a-button>
                </div>
                <div v-if="selectedItems.length" class="selected-list">
                    <div v-for="(item, index) in selectedItems" :key="item.goodsId" class="selected-card">
                        <div class="selected-sort">
                            <a-button size="small" icon="arrow-up" :disabled="index === 0" @click="moveSelected(index, -1)" />
                            <a-button size="small" icon="arrow-down" :disabled="index === selectedItems.length - 1" @click="moveSelected(index, 1)" />
                        </div>
                        <div class="selected-thumb">
                            <img v-if="item.image" :src="image(item.image)" :alt="item.goodsName" />
                            <a-icon v-else type="picture" />
                        </div>
                        <div class="selected-info">
                            <div class="selected-name">{{ item.goodsName || '未命名商品' }}</div>
                            <div class="selected-meta">{{ item.goodsNo || item.goodsId }}</div>
                            <div class="selected-price">¥{{ getGoodsPrice(item) }}</div>
                        </div>
                        <a-button type="link" icon="delete" class="selected-remove" @click="removeSelected(index)" />
                    </div>
                </div>
                <a-empty v-else description="从左侧选择商品" />
            </div>
        </div>

        <div slot="footer" class="picker-footer">
            <span>已选 {{ selectedItems.length }} 个商品</span>
            <div>
                <a-button @click="close">取消</a-button>
                <a-button type="primary" @click="confirm">确定</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import { image } from '@/utils/Tool'
import { selectMallDecorateGoods } from '@/api/mallDecorateV2'

export default {
    name: 'MallV2GoodsPicker',
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        value: {
            type: Array,
            default: () => []
        },
        storeShopId: {
            type: [String, Number],
            default: ''
        },
        categoryList: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            loading: false,
            loadingMore: false,
            goodsList: [],
            selectedItems: [],
            hasMore: true,
            query: {
                keyword: '',
                storeGoodsTypeId: undefined,
                pageNo: 1,
                pageSize: 20
            }
        }
    },
    watch: {
        visible(value) {
            if (value) {
                this.open()
            }
        }
    },
    methods: {
        image,
        open() {
            this.selectedItems = this.normalizeGoodsList(this.value)
            this.query.pageNo = 1
            this.hasMore = true
            this.loadGoods(true)
        },
        normalizeGoodsList(list) {
            return (Array.isArray(list) ? list : []).filter(item => item && item.goodsId).map(item => ({
                goodsId: item.goodsId,
                goodsNo: item.goodsNo,
                goodsName: item.goodsName,
                image: item.image || item.imageUrl,
                unitPrice: item.unitPrice,
                retailPrice: item.retailPrice
            }))
        },
        loadGoods(reset) {
            if (!this.storeShopId) {
                return
            }
            if (reset) {
                this.query.pageNo = 1
                this.loading = true
            } else {
                this.loadingMore = true
            }
            selectMallDecorateGoods({
                loadType: 1,
                storeShopId: this.storeShopId,
                storeGoodsTypeId: this.query.storeGoodsTypeId,
                keyword: this.query.keyword,
                pageNo: this.query.pageNo,
                pageSize: this.query.pageSize
            }).then(res => {
                const list = res.success ? (res.result || []) : []
                this.goodsList = reset ? list : this.goodsList.concat(list)
                this.hasMore = list.length >= this.query.pageSize
            }).finally(() => {
                this.loading = false
                this.loadingMore = false
            })
        },
        searchGoods() {
            this.loadGoods(true)
        },
        changeCategory(value) {
            this.query.storeGoodsTypeId = value
            this.searchGoods()
        },
        loadMore() {
            this.query.pageNo += 1
            this.loadGoods(false)
        },
        isSelected(goodsId) {
            return this.selectedItems.some(item => item.goodsId === goodsId)
        },
        toggleGoods(goods) {
            const index = this.selectedItems.findIndex(item => item.goodsId === goods.goodsId)
            if (index >= 0) {
                this.selectedItems.splice(index, 1)
                return
            }
            this.selectedItems.push(this.normalizeGoodsList([goods])[0])
        },
        removeSelected(index) {
            this.selectedItems.splice(index, 1)
        },
        clearSelected() {
            this.selectedItems = []
        },
        moveSelected(index, delta) {
            const target = index + delta
            if (target < 0 || target >= this.selectedItems.length) {
                return
            }
            const item = this.selectedItems[index]
            this.selectedItems.splice(index, 1)
            this.selectedItems.splice(target, 0, item)
        },
        getGoodsPrice(goods) {
            const price = goods && (goods.unitPrice !== undefined && goods.unitPrice !== null ? goods.unitPrice : goods.retailPrice)
            return price !== undefined && price !== null && price !== '' ? price : '0'
        },
        confirm() {
            this.$emit('select', this.normalizeGoodsList(this.selectedItems))
            this.close()
        },
        close() {
            this.$emit('update:visible', false)
        }
    }
}
</script>

<style scoped>
.goods-picker-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    gap: 16px;
}

.goods-picker-toolbar {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
}

.goods-search {
    flex: 1;
}

.goods-category {
    width: 180px;
}

.goods-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-content: start;
    align-items: start;
    gap: 10px;
    min-height: 430px;
    max-height: 430px;
    overflow-y: auto;
}

.goods-card {
    position: relative;
    display: flex;
    gap: 10px;
    height: 96px;
    min-width: 0;
    padding: 10px;
    border: 1px solid #edf0f5;
    border-radius: 6px;
    background: #ffffff;
    cursor: pointer;
}

.goods-card.active {
    border-color: #d64545;
    box-shadow: 0 0 0 2px rgba(214, 69, 69, 0.1);
}

.goods-thumb,
.selected-thumb {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 76px;
    height: 76px;
    border-radius: 6px;
    background: #f5f6f8;
    overflow: hidden;
}

.goods-thumb img,
.selected-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.goods-info,
.selected-info {
    min-width: 0;
    flex: 1;
}

.goods-info {
    padding-right: 42px;
}

.goods-name,
.selected-name {
    overflow: hidden;
    color: #1f2937;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.goods-no,
.selected-meta {
    margin-top: 6px;
    overflow: hidden;
    color: #8c96a5;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.goods-price,
.selected-price {
    margin-top: 8px;
    color: #d64545;
    font-weight: 700;
}

.goods-selected-tag {
    position: absolute;
    right: 6px;
    bottom: 8px;
}

.goods-load-more {
    height: 38px;
    padding-top: 10px;
    color: #8c96a5;
    text-align: center;
}

.goods-selected-panel {
    min-height: 520px;
    padding-left: 16px;
    border-left: 1px solid #edf0f5;
}

.selected-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 32px;
    margin-bottom: 8px;
    color: #1f2937;
    font-weight: 600;
}

.selected-list {
    max-height: 486px;
    overflow-y: auto;
}

.selected-card {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    padding: 8px 0;
    border-bottom: 1px solid #f0f2f5;
}

.selected-sort {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.selected-remove {
    flex: 0 0 auto;
    color: #ff4d4f;
}

.picker-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
</style>
